import React from 'react';
import {
  Grid2,
  Card,
  CardContent,
  Typography,
  Box,
  CardMedia,
  Container,
} from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import FeedbackButtons from './FeedbackButtons';

// Function to determine if a result is an individual apartment listing
const isIndividualListing = (result) => {
  // Score the result based on various factors
  let score = 0;
  
  // First check if it's a sale listing - if so, return false immediately
  const title = result.title.toLowerCase();
  const snippet = result.snippet.toLowerCase();
  const saleKeywords = ['na sprzedaż', 'sprzedam', 'sprzedaż', 'do sprzedaży', 'na sprzedaz', 'sprzedaz'];
  
  if (saleKeywords.some(keyword => title.includes(keyword) || snippet.includes(keyword))) {
    return false;
  }
  
  // Extract room number from title if not already present
  if (!result.rooms) {
    // Match patterns like "1 pokój", "2 pokoje", "3-pokojowe", etc.
    const roomMatch = title.match(/(\d+)\s*(?:-|\s)?(?:pok[óo]j|pokoi|pokoj[oó]w|pokojowe)/i);
    if (roomMatch) {
      result.rooms = parseInt(roomMatch[1], 10);
    }
  }

  // Extract square meters if not already present
  if (!result.squareMeters) {
    // Match patterns like "50m2", "50 m²", "50m²", etc.
    const squareMatch = title.match(/(\d+)\s*(?:m2|m²|m\s*2)/i);
    if (squareMatch) {
      result.squareMeters = parseInt(squareMatch[1], 10);
    }
  }
  
  // Check URL patterns that indicate individual listings
  const url = result.link.toLowerCase();
  
  // otodom.pl specific patterns
  if (url.includes('otodom.pl/pl/oferta/')) {
    score += 3; // Higher score for otodom.pl listings
    // Check for specific apartment details in the URL
    if (url.includes('mieszkanie') || url.includes('apartament')) {
      score += 1;
    }
    // Check for size in URL (e.g., 96m2)
    if (/\d+m2/.test(url)) {
      score += 1;
    }
  }
  
  // olx.pl specific patterns
  if (url.includes('olx.pl/d/oferta/')) {
    score += 3; // Higher score for olx.pl listings
    // Check for specific apartment details in the URL
    if (url.includes('mieszkanie') || url.includes('apartament')) {
      score += 1;
    }
    // Check for room count in URL
    if (/\d+-pokoj/.test(url)) {
      score += 1;
    }
  }

  // Check if URL contains an ID (common for individual listings)
  if (/\bID[a-zA-Z0-9]+\b/.test(url)) {
    score += 2; // Higher score for URLs with ID
  }

  // Check if the title contains specific apartment details
  if (title.includes('m²') || 
      title.includes('metr') ||
      title.includes('pokój') ||
      title.includes('pokoi') ||
      title.includes('pokoj')) {
    score += 1;
  }

  // Check if we have specific apartment details in the result
  if (result.price) score += 2; // Increased weight for price
  if (result.squareMeters) score += 2; // Increased weight for size
  if (result.rooms) score += 2; // Increased weight for rooms

  // Check if the snippet contains specific apartment details
  if (snippet.includes('m²') || 
      snippet.includes('metr') ||
      snippet.includes('pokój') ||
      snippet.includes('pokoi') ||
      snippet.includes('czynsz') ||
      snippet.includes('kaucja') ||
      snippet.includes('umeblowane') ||
      snippet.includes('nieumeblowane')) {
    score += 1;
  }

  // Check if the source is a known apartment listing site
  const source = result.source.toLowerCase();
  if (source.includes('otodom.pl')) {
    score += 2; // Higher score for otodom.pl
  } else if (source.includes('olx.pl')) {
    score += 2; // Higher score for olx.pl
  } else if (source.includes('facebook.com/marketplace')) {
    score += 1;
  }

  // Check if the result is from a Facebook group
  if (source.includes('facebook.com/groups/')) {
    // Additional checks for Facebook group posts
    if (snippet.includes('wynajem') || 
        snippet.includes('najem') ||
        snippet.includes('wynajme') ||
        snippet.includes('do wynajecia') ||
        snippet.includes('do wynajęcia')) {
      score += 2; // Boost score for relevant Facebook group posts
    }
  }

  // Check for Polish-specific keywords
  const polishKeywords = [
    'mieszkanie', 'apartament', 'kawalerka', 'wynajem', 'najem',
    'sypialnia', 'łazienka', 'kuchnia', 'salon', 'balkon', 'taras',
    'umeblowane', 'nieumeblowane', 'dostępne', 'do wynajęcia', 'wynajme',
    'dlugoterminowy','czynsz', 'kaucja', 'opłaty', 'media', 'prąd', 'woda', 'gaz',
    'internet', 'telewizja', 'parking', 'garaż', 'piwnica', 'strych'
  ];
  
  polishKeywords.forEach(keyword => {
    if (title.includes(keyword) || snippet.includes(keyword)) {
      score += 1;
    }
  });

  // Return true if the score is high enough to be considered an individual listing
  return score >= 3; // Increased threshold for better filtering
};

const AIAgentSearch = ({ results, filters = {} }) => {
  if (!results || results.length === 0) {
    return (
      <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
        No AI-powered results found. Try adjusting your search criteria.
      </Typography>
    );
  }

  // Filter and sort results
  const filteredResults = results
    .filter(result => {
      // First check if it's an individual listing
      if (!isIndividualListing(result)) return false;

      // Apply square meter filter if present
      if (filters.min_square_footage && result.squareMeters < filters.min_square_footage) return false;
      if (filters.max_square_footage && result.squareMeters > filters.max_square_footage) return false;

      // Apply rooms filter if present
      if (filters.rooms && result.rooms !== parseInt(filters.rooms, 10)) return false;

      return true;
    })
    .map(result => {
      // Ensure we have a proper date object
      const date = result.lastUpdatedDate ? new Date(result.lastUpdatedDate) : null;
      return { ...result, parsedDate: date };
    })
    .sort((a, b) => {
      // If both have dates, compare them
      if (a.parsedDate && b.parsedDate) {
        return b.parsedDate - a.parsedDate;
      }
      
      // If only one has a date, prioritize it
      if (a.parsedDate) return -1;
      if (b.parsedDate) return 1;
      
      // If neither has a date, sort by completeness
      const aScore = (a.price ? 1 : 0) + (a.squareMeters ? 1 : 0) + (a.rooms ? 1 : 0);
      const bScore = (b.price ? 1 : 0) + (b.squareMeters ? 1 : 0) + (b.rooms ? 1 : 0);
      return bScore - aScore;
    });

  if (filteredResults.length === 0) {
    return (
      <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
        No individual apartment listings found. Try adjusting your search criteria.
      </Typography>
    );
  }

  return (
    <Container>
      <Grid2 container spacing={4}>
        {filteredResults.map((result, index) => (
          <Grid2 item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: 300,
                minWidth: 300,
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: { xs: 'none', sm: 'scale(1.05)' },
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              <Box
                onClick={() => window.open(result.link, '_blank')}
                sx={{ 
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
              >
                <CardMedia
                  component="img"
                  image={result.image || result.pagemap?.cse_image?.[0]?.src || result.pagemap?.cse_thumbnail?.[0]?.src || '/placeholder.png'}
                  alt={result.title || 'Apartment image'}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 180,
                    backgroundColor: 'grey.300',
                    overflow: 'hidden',
                    objectFit: 'cover',
                  }}
                  onError={(e) => {
                    e.target.src = '/placeholder.png';
                  }}
                />

                <CardContent
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 2,
                    flex: 1,
                  }}
                >
                  <Typography
                    variant="h7"
                    sx={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                      lineHeight: 1.9,
                      minHeight: '1.9em',
                    }}
                  >
                    {result.title}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, minHeight: '24px' }}>
                    <SquareFootIcon color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                      {result.squareMeters ? `${result.squareMeters} m²` : 'm2'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, minHeight: '24px' }}>
                    <MeetingRoomIcon color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                      {result.rooms ? `${result.rooms} rooms` : ''}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      minHeight: '2.5em',
                      flex: 1,
                    }}
                  >
                    {result.snippet}
                  </Typography>
                </CardContent>
              </Box>

              <Box 
                sx={{ 
                  p: 2, 
                  pt: 0,
                  display: 'flex', 
                  justifyContent: 'flex-end',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  mt: 'auto'
                }}
              >
                <FeedbackButtons result={result} />
              </Box>
            </Card>
          </Grid2>
        ))}
      </Grid2>
    </Container>
  );
};

export default AIAgentSearch; 